class CwcMaterialDirectory extends HTMLElement {
  async connectedCallback() {
    this.input = this.querySelector('[data-material-search]');
    this.submit = this.querySelector('[data-material-submit]');
    this.clear = this.querySelector('[data-material-clear]');
    this.quickSearches = [...this.querySelectorAll('[data-material-quick-search]')];
    this.count = this.querySelector('[data-material-count]');
    this.empty = this.querySelector('[data-material-empty]');
    this.grid = this.querySelector('[data-material-grid]');
    this.bar = this.querySelector('[data-material-bar]');
    this.results = this.querySelector('[data-material-results]');

    this.input?.addEventListener('input', () => this.render());
    this.input?.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      this.render(true);
    });
    this.submit?.addEventListener('click', () => this.render(true));
    this.clear?.addEventListener('click', () => this.resetSearch());
    this.quickSearches.forEach((button) => {
      button.addEventListener('click', () => {
        if (!this.input) return;
        this.input.value = button.dataset.materialQuickSearch || '';
        this.render(true);
      });
    });

    if (this.dataset.nextPage) await this.loadAdditionalPages(this.dataset.nextPage);
    this.cards = [...this.querySelectorAll('[data-material-card]')];
    this.hydrateImages();
    this.render();
  }

  async loadAdditionalPages(nextUrl) {
    if (!this.grid) return;
    let url = nextUrl;

    while (url) {
      try {
        const response = await fetch(url);
        if (!response.ok) break;
        const documentCopy = new DOMParser().parseFromString(await response.text(), 'text/html');
        const directory = documentCopy.querySelector('cwc-material-directory');
        const cards = directory?.querySelectorAll('[data-material-card]') || [];
        cards.forEach((card) => this.grid.appendChild(card));
        url = directory?.dataset.nextPage || '';
      } catch (error) {
        break;
      }
    }
  }

  normalize(value) {
    return String(value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();
  }

  render(shouldScroll = false) {
    const query = this.normalize(this.input?.value);

    if (!query) {
      this.cards?.forEach((card) => { card.hidden = true; });
      if (this.results) this.results.hidden = true;
      if (this.grid) this.grid.hidden = true;
      if (this.bar) this.bar.hidden = true;
      if (this.clear) this.clear.hidden = true;
      if (this.empty) this.empty.hidden = true;
      return;
    }

    let visible = 0;
    this.cards?.forEach((card) => {
      const searchText = this.normalize(card.dataset.search);
      const show = searchText.includes(query);
      card.hidden = !show;
      if (show) visible += 1;
    });

    if (this.results) this.results.hidden = false;
    if (this.grid) this.grid.hidden = false;
    if (this.bar) this.bar.hidden = false;
    if (this.clear) this.clear.hidden = false;
    if (this.count) this.count.textContent = `${visible} ${visible === 1 ? 'material' : 'materials'} found`;
    if (this.empty) this.empty.hidden = visible !== 0;

    if (shouldScroll && this.bar) {
      const offset = 92;
      const top = this.bar.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  }

  resetSearch() {
    if (!this.input) return;
    this.input.value = '';
    this.render();
    this.input.focus();
    const top = this.input.getBoundingClientRect().top + window.scrollY - 120;
    window.scrollTo({ top, behavior: 'smooth' });
  }

  hydrateImages() {
    const images = [...this.querySelectorAll('[data-product-handle]')];
    const load = async (image) => {
      if (!image.dataset.productHandle || image.dataset.imageLoaded) return;
      image.dataset.imageLoaded = 'true';

      try {
        const response = await fetch(`/products/${image.dataset.productHandle}.js`);
        if (!response.ok) return;
        const product = await response.json();
        if (!product.featured_image) return;
        image.src = product.featured_image.startsWith('//') ? `https:${product.featured_image}` : product.featured_image;
        image.alt = product.title || image.alt;
      } catch (error) {
        image.hidden = true;
      }
    };

    if (!('IntersectionObserver' in window)) {
      images.forEach(load);
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        observer.unobserve(entry.target);
        load(entry.target);
      });
    }, { rootMargin: '500px 0px' });

    images.forEach((image) => observer.observe(image));
  }
}

if (!customElements.get('cwc-material-directory')) {
  customElements.define('cwc-material-directory', CwcMaterialDirectory);
}
